import * as React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Award, BadgeCheck, Clock, Undo2, Users } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { formatDate } from '@/lib/format'
import type {
  Activity,
  Certification,
  CertificationPresentation,
  CertificationTest,
  StudentCertification,
  TeamMember,
  TestResult,
} from '@/lib/types'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { EmptyState, ErrorNote, Loading, Page } from '@/components/page'
import { ProgramBadge } from '@/components/program-badge'

/**
 * Every student's standing on every program this team runs.
 *
 * COACH ONLY, and the database agrees: `student_certifications` and
 * `test_results` are readable by their owner or by `app.is_dpl_coach`, so a
 * lead who reached this URL would see an empty page rather than a filtered one.
 * The route guard in App.tsx is the courtesy; the policy is the control.
 *
 * The counts here are recomputed from completions and results rather than read
 * from the status row. Both come from the same query, and showing "in process"
 * beside 18 of 18 finished steps would be the kind of small lie that makes
 * people stop trusting a dashboard.
 */
export function CertificationStatusPage() {
  const { currentTeam } = useAuth()
  const teamId = currentTeam?.id
  const [busyId, setBusyId] = React.useState<string | null>(null)

  const { data, loading, error, refetch } = useQuery(async () => {
    const empty = {
      certs: [] as Certification[],
      members: [] as TeamMember[],
      standing: [] as StudentCertification[],
      decks: [] as CertificationPresentation[],
      tests: [] as CertificationTest[],
      activities: [] as Activity[],
      completions: [] as Array<{ activity_id: string; user_id: string }>,
      results: [] as TestResult[],
    }
    if (!teamId) return empty

    const enabled = await sb()
      .from('team_certifications')
      .select('certification_id')
      .eq('team_id', teamId)
    if (enabled.error) throw enabled.error
    const certIds = (enabled.data ?? []).map((r) => r.certification_id as string)
    if (certIds.length === 0) return empty

    const [certs, members, standing, decks, tests, activities, completions, results] =
      await Promise.all([
        sb().from('certifications').select('*').in('id', certIds).order('display_order'),
        sb().from('team_members').select('*').eq('team_id', teamId).eq('active', true).order('email'),
        sb().from('student_certifications').select('*').eq('team_id', teamId),
        sb().from('certification_presentations').select('*').in('certification_id', certIds),
        sb().from('certification_tests').select('*').in('certification_id', certIds),
        sb().from('activities').select('*').eq('team_id', teamId),
        sb().from('activity_completions').select('activity_id, user_id').eq('team_id', teamId),
        sb().from('test_results').select('*').eq('team_id', teamId),
      ])
    for (const r of [certs, members, standing, decks, tests, activities, completions, results]) {
      if (r.error) throw r.error
    }
    return {
      certs: (certs.data ?? []) as Certification[],
      members: (members.data ?? []) as TeamMember[],
      standing: (standing.data ?? []) as StudentCertification[],
      decks: (decks.data ?? []) as CertificationPresentation[],
      tests: (tests.data ?? []) as CertificationTest[],
      activities: (activities.data ?? []) as Activity[],
      completions: (completions.data ?? []) as Array<{ activity_id: string; user_id: string }>,
      results: (results.data ?? []) as TestResult[],
    }
  }, [teamId])

  /**
   * Approve, or take it back.
   *
   * Both go through an RPC rather than an update: the transition has to stamp
   * WHO approved and WHEN in the same statement that moves the status, and the
   * function checks `app.is_dpl_coach` itself. A policy on the table could not
   * do either (migrations/0039).
   */
  async function decide(id: string, approve: boolean) {
    setBusyId(id)
    try {
      const { error: err } = await sb().rpc(
        approve ? 'approve_student_certification' : 'unapprove_student_certification',
        { p_id: id },
      )
      if (err) throw err
      await refetch()
    } catch (err) {
      window.alert(err instanceof Error ? err.message : String(err))
    } finally {
      setBusyId(null)
    }
  }

  /** Steps done, out of steps required, for one person on one path. */
  function progress(certId: string, userId: string | null) {
    if (!userId || !data) return { done: 0, total: 0 }
    const deckIds = data.decks.filter((d) => d.certification_id === certId).map((d) => d.presentation_id)
    const testIds = data.tests.filter((t) => t.certification_id === certId).map((t) => t.test_id)

    const walked = deckIds.filter((pid) => {
      const activity = data.activities.find((a) => a.presentation_id === pid)
      return activity && data.completions.some((c) => c.activity_id === activity.id && c.user_id === userId)
    }).length
    const passed = testIds.filter((tid) =>
      data.results.some((r) => r.test_id === tid && r.user_id === userId && r.passed),
    ).length

    return { done: walked + passed, total: deckIds.length + testIds.length }
  }

  if (!currentTeam) return null

  return (
    <Page
      title="Program status"
      description={`Everyone on ${currentTeam.name}. Visible to DPL coaches only.`}
      actions={
        <Button variant="outline" size="sm" asChild>
          <Link to="/certifications">
            <ArrowLeft /> My path
          </Link>
        </Button>
      }
    >
      {error && <ErrorNote error={error} />}
      {loading && !data ? (
        <Loading />
      ) : (data?.certs.length ?? 0) === 0 ? (
        <EmptyState
          icon={Award}
          title="No programs for this team"
          description="Turn one on in Admin. Programs are off for a team until a coach enables them."
          action={
            <Button asChild>
              <Link to="/admin">Open Admin</Link>
            </Button>
          }
        />
      ) : (
        <div className="space-y-8">
          {data!.certs.map((cert) => {
            const rows = data!.members.map((m) => {
              const p = progress(cert.id, m.user_id)
              const status = data!.standing.find(
                (s) => s.certification_id === cert.id && s.user_id === m.user_id,
              )
              return { member: m, ...p, status }
            })
            // Counted from the STATUS now, not from the step tally: finishing
            // the work no longer means finished, and a summary that said
            // "12 complete" while twelve people waited on a coach would be the
            // most misleading line on the page.
            const complete = rows.filter((r) => r.status?.status === 'complete').length
            const waiting = rows.filter((r) => r.status?.status === 'awaiting_approval').length
            const started = rows.filter(
              (r) => r.status?.status === 'in_process' && r.done > 0,
            ).length

            return (
              <section key={cert.id} className="space-y-3">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <h2 className="text-base font-semibold">{cert.title}</h2>
                  <span className="text-xs text-muted-foreground">
                    <Users className="mr-1 inline h-3 w-3" />
                    {complete} approved · {waiting} waiting for approval · {started} in process ·{' '}
                    {rows.length - complete - waiting - started} not started
                  </span>
                  {waiting > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-warning/50 px-2 py-0.5 text-[11px] font-medium text-warning">
                      <Clock className="h-3 w-3" />
                      {waiting} waiting on you
                    </span>
                  )}
                </div>

                <Card>
                  <CardContent className="divide-y p-0">
                    {rows.map(({ member, done, total, status }) => {
                      const pct = total ? Math.round((done / total) * 100) : 0
                      return (
                        <div key={member.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium leading-tight">
                              {member.display_name || member.email}
                            </div>
                            <div className="truncate text-xs text-muted-foreground">
                              {!member.user_id
                                ? 'Has not signed in yet'
                                : status?.status === 'complete'
                                  ? `Approved ${status.approved_at ? formatDate(status.approved_at) : ''}`
                                  : status?.status === 'awaiting_approval'
                                    ? 'Every step done — waiting for your approval'
                                    : done > 0
                                      ? `Started ${status ? formatDate(status.started_at) : ''}`
                                      : 'Not started'}
                            </div>
                          </div>

                          <div className="hidden w-40 sm:block">
                            <div className="h-2 overflow-hidden rounded-full bg-muted">
                              <div
                                className={cn(
                                  'h-full rounded-full transition-[width]',
                                  done === total && total > 0 ? 'bg-primary' : 'bg-primary/60',
                                )}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>

                          <span className="w-16 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                            {done}/{total}
                          </span>
                          <Badge
                            variant={status?.status === 'complete' ? 'default' : 'muted'}
                            className={cn(
                              'w-40 shrink-0 justify-center',
                              status?.status === 'awaiting_approval' &&
                                'border-warning/50 text-warning',
                            )}
                          >
                            {status?.status === 'complete'
                              ? 'Complete'
                              : status?.status === 'awaiting_approval'
                                ? 'Waiting for approval'
                                : done > 0
                                  ? 'In process'
                                  : 'Not started'}
                          </Badge>

                          {/* The decision itself. Present only where there is
                              one to make — a row still in process has nothing
                              for a coach to do. */}
                          <div className="w-28 shrink-0 text-right">
                            {status?.status === 'awaiting_approval' && (
                              <Button
                                size="sm"
                                disabled={busyId === status.id}
                                onClick={() => void decide(status.id, true)}
                              >
                                <BadgeCheck /> Approve
                              </Button>
                            )}
                            {status?.status === 'complete' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={busyId === status.id}
                                onClick={() => void decide(status.id, false)}
                                title="Withdraw the approval and send it back"
                              >
                                <Undo2 /> Undo
                              </Button>
                            )}
                          </div>

                          {/* Their badge, on its own line under the row. Small,
                              and a link to the public verification page, which
                              is the same page the QR opens. */}
                          {status?.status === 'complete' && (
                            <a
                              href={`/badges/${status.badge_id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="basis-full"
                              title="Open the verification page"
                            >
                              <ProgramBadge
                                title={cert.title}
                                slug={cert.slug}
                                completedAt={status.approved_at ?? status.completed_at}
                                badgeId={status.badge_id}
                                className="max-w-[240px]"
                              />
                            </a>
                          )}
                        </div>
                      )
                    })}
                  </CardContent>
                </Card>
              </section>
            )
          })}
        </div>
      )}
    </Page>
  )
}
